import {
  formatCurrency,
  MAX_INSTALLMENTS,
  PAYMENT_METHOD_LABELS,
  type PaymentMethodValue,
} from '@crm/shared';
import { Button, cn, Input, Label, Modal, Select } from '@crm/ui';
import { Banknote, CreditCard, Plus, Smartphone, Trash2, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toErrorMessage } from '@/modules/auth/utils';
import { useMachines } from '../api';

const METHOD_ICONS: Record<PaymentMethodValue, React.ComponentType<{ className?: string }>> = {
  CASH: Banknote,
  PIX: Smartphone,
  DEBIT: Wallet,
  CREDIT: CreditCard,
};

const METHODS: PaymentMethodValue[] = ['CASH', 'PIX', 'DEBIT', 'CREDIT'];

interface SaleLine {
  key: string;
  method: PaymentMethodValue;
  machineId: string;
  installments: number;
  amount: number;
}

const round = (value: number) => Math.round(value * 100) / 100;

interface PaymentSheetProps {
  open: boolean;
  /** Quanto falta receber pelo atendimento. */
  total: number;
  title: string;
  loading?: boolean;
  error?: unknown;
  onClose: () => void;
  onConfirm: (
    payments: {
      method: PaymentMethodValue;
      machineId?: string;
      installments: number;
      amount: number;
    }[],
  ) => void;
}

/**
 * Como o cliente pagou, em um toque: a forma mais usada ja vem escolhida e o
 * valor ja vem preenchido com o total. Mais de uma linha e pagamento misto.
 */
export function PaymentSheet({
  open,
  total,
  title,
  loading = false,
  error,
  onClose,
  onConfirm,
}: PaymentSheetProps) {
  const machines = useMachines(true);
  // A lista ja vem ordenada pela mais usada - a primeira e o palpite certo.
  const defaultMachine = machines.data?.data[0]?.id ?? '';

  const [lines, setLines] = useState<SaleLine[]>([]);

  useEffect(() => {
    if (!open) return;

    setLines([
      { key: 'first', method: 'PIX', machineId: defaultMachine, installments: 1, amount: total },
    ]);
  }, [open, total, defaultMachine]);

  const paid = round(lines.reduce((sum, line) => sum + line.amount, 0));
  const missing = round(total - paid);

  function setLine(key: string, patch: Partial<SaleLine>) {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  }

  function addLine() {
    setLines((current) => [
      ...current,
      {
        key: `line-${current.length + 1}`,
        method: 'CASH',
        machineId: defaultMachine,
        installments: 1,
        amount: Math.max(0, missing),
      },
    ]);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Como o cliente pagou?"
      subtitle={title}
      actions={[
        { label: 'Voltar', variant: 'ghost', onClick: onClose },
        {
          label: 'Encerrar e receber',
          loading,
          disabled: lines.length === 0 || paid <= 0,
          onClick: () =>
            onConfirm(
              lines.map((line) => ({
                method: line.method,
                machineId: line.method === 'CASH' ? undefined : line.machineId || undefined,
                installments: line.method === 'CREDIT' ? line.installments : 1,
                amount: line.amount,
              })),
            ),
        },
      ]}
    >
      <div className="space-y-4">
        {error ? <p className="text-sm text-destructive">{toErrorMessage(error)}</p> : null}

        <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-3">
          <span className="text-sm text-muted-foreground">Total do atendimento</span>
          <span className="text-xl font-semibold">{formatCurrency(total)}</span>
        </div>

        {lines.map((line, index) => (
          <div key={line.key} className="space-y-3 rounded-lg border border-border p-3">
            {/* Botoes grandes: a escolha da forma e feita com o polegar. */}
            <div className="grid grid-cols-4 gap-2">
              {METHODS.map((method) => {
                const Icon = METHOD_ICONS[method];
                const active = line.method === method;

                return (
                  <button
                    key={method}
                    type="button"
                    onClick={() => setLine(line.key, { method })}
                    className={cn(
                      'flex h-16 flex-col items-center justify-center gap-1 rounded-lg border text-xs transition-colors',
                      active
                        ? 'border-primary bg-primary/10 font-medium text-primary'
                        : 'border-border text-muted-foreground hover:bg-muted',
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {PAYMENT_METHOD_LABELS[method]}
                  </button>
                );
              })}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor={`amount-${line.key}`}>Valor</Label>
                <Input
                  id={`amount-${line.key}`}
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  prefix="R$"
                  className="h-12 text-right text-lg"
                  value={line.amount}
                  onChange={(event) =>
                    setLine(line.key, { amount: Number(event.target.value || 0) })
                  }
                />
              </div>

              {line.method === 'CREDIT' ? (
                <div className="space-y-2">
                  <Label htmlFor={`installments-${line.key}`}>Parcelas</Label>
                  <Select
                    id={`installments-${line.key}`}
                    className="h-12"
                    value={line.installments}
                    onChange={(event) =>
                      setLine(line.key, { installments: Number(event.target.value) })
                    }
                  >
                    {Array.from({ length: MAX_INSTALLMENTS }, (_, index) => index + 1).map(
                      (installments) => (
                        <option key={installments} value={installments}>
                          {installments}x de {formatCurrency(line.amount / installments)}
                        </option>
                      ),
                    )}
                  </Select>
                </div>
              ) : null}
            </div>

            {line.method === 'CASH' ? null : (
              <div className="space-y-2">
                <Label htmlFor={`machine-${line.key}`}>Maquininha</Label>
                <Select
                  id={`machine-${line.key}`}
                  className="h-12"
                  value={line.machineId}
                  onChange={(event) => setLine(line.key, { machineId: event.target.value })}
                >
                  {(machines.data?.data ?? []).map((machine) => (
                    <option key={machine.id} value={machine.id}>
                      {machine.label}
                    </option>
                  ))}
                </Select>
                {(machines.data?.data ?? []).length === 0 ? (
                  <p className="text-xs text-destructive">
                    Nenhuma maquininha marcada em Recebimentos - Maquininhas.
                  </p>
                ) : null}
              </div>
            )}

            {lines.length > 1 ? (
              <Button
                variant="ghost"
                className="text-muted-foreground hover:text-destructive"
                onClick={() =>
                  setLines((current) => current.filter((entry) => entry.key !== line.key))
                }
              >
                <Trash2 className="h-4 w-4" />
                Remover forma {index + 1}
              </Button>
            ) : null}
          </div>
        ))}

        <div className="flex items-center justify-between gap-3">
          <Button variant="outline" onClick={addLine}>
            <Plus className="h-4 w-4" />
            Outra forma
          </Button>

          <span
            className={cn(
              'text-sm',
              missing === 0 ? 'text-muted-foreground' : 'font-medium text-destructive',
            )}
          >
            {missing === 0
              ? 'Total fechado'
              : missing > 0
                ? `Faltam ${formatCurrency(missing)}`
                : `${formatCurrency(-missing)} a mais`}
          </span>
        </div>
      </div>
    </Modal>
  );
}
